import React, { useEffect, useState } from "react";
import { ExternalLink, RefreshCw, Trash2, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORY_LABEL = {
    alojamiento: "Alojamiento",
    evento: "Evento",
    actividad: "Actividad",
    gastronomia: "Gastronomía",
    cultura: "Cultura",
    otro: "Otro",
};

export default function TarifaNewsManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tarifa-news");
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const runRefresh = async () => {
        setRefreshing(true);
        setMessage(null);
        setError("");
        try {
            const { data } = await api.post("/admin/tarifa-news/refresh");
            setMessage(`Refresco completado: ${data.fetched} noticias analizadas · ${data.kept} aprobadas · ${data.inserted} nuevas añadidas.`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setRefreshing(false);
        }
    };

    const remove = async (n) => {
        if (!window.confirm("¿Eliminar esta noticia?")) return;
        try {
            await api.delete(`/admin/tarifa-news/${n.id}`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div data-testid="admin-tarifa-news">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#082050]/70">Administración</div>
                    <h1 className="mt-2 text-3xl font-semibold text-[#082050] tracking-tight">
                        Tarifa en los medios
                    </h1>
                    <p className="mt-2 text-sm text-[#4A5568]">
                        Noticias externas sobre Tarifa curadas por IA. Se actualiza
                        automáticamente cada 6 horas.
                    </p>
                </div>
                <button
                    onClick={runRefresh}
                    disabled={refreshing}
                    data-testid="btn-refresh-tarifa-news"
                    className="bg-[#082050] text-white px-5 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#082050]/90 disabled:opacity-50"
                >
                    <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                    {refreshing ? "Analizando..." : "Refrescar ahora"}
                </button>
            </div>

            {message && (
                <div className="mb-6 text-sm text-[#082050] border border-[#082050] bg-[#082050]/5 px-4 py-3 inline-flex items-center gap-2">
                    <Sparkles size={14} /> {message}
                </div>
            )}
            {error && (
                <div className="mb-6 text-sm text-red-600 border border-red-500 bg-red-50 px-4 py-3">
                    {error}
                </div>
            )}

            <div className="space-y-4">
                {loading && <div className="text-[#4A5568]">Cargando...</div>}
                {!loading && items.length === 0 && (
                    <div className="text-[#4A5568] bg-white border border-[#E2E4E9] p-8 text-center">
                        Sin noticias curadas todavía. Pulsa "Refrescar ahora" para lanzar el primer ciclo.
                    </div>
                )}
                {items.map((n, i) => (
                    <article
                        key={n.id}
                        data-testid={`admin-tarifa-news-item-${i}`}
                        className="bg-white border border-[#E2E4E9] p-6"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
                                    <span className="text-[#082050]/70">
                                        {CATEGORY_LABEL[n.category] || n.category}
                                    </span>
                                    <span className="text-[#718096]">· {n.sentiment}</span>
                                </div>
                                <h3 className="mt-2 text-base font-semibold text-[#082050] leading-snug">
                                    {n.title}
                                </h3>
                                {n.summary && (
                                    <p className="mt-2 text-sm text-[#4A5568] leading-relaxed">
                                        {n.summary}
                                    </p>
                                )}
                                <div className="mt-3 text-xs text-[#718096]">
                                    {n.source || "Fuente desconocida"}
                                </div>
                            </div>
                            <div className="flex flex-col gap-2 items-end">
                                <a
                                    href={n.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-[#082050] hover:opacity-70 inline-flex items-center gap-1"
                                >
                                    Ver <ExternalLink size={11} />
                                </a>
                                <button
                                    onClick={() => remove(n)}
                                    data-testid={`admin-tarifa-news-delete-${i}`}
                                    className="text-xs text-red-600 hover:opacity-70 inline-flex items-center gap-1"
                                >
                                    <Trash2 size={11} /> Eliminar
                                </button>
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}
